import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  createStyles,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Switch,
  TextField,
  Theme,
  Typography,
  IconButton,
  Input,
  useTheme,
} from "@material-ui/core";
import clsx from 'clsx';
import slash from 'slash';
import { dialog } from "../electron-modules";
import LocalScene from "../viewer/js/projections";
import { UnitConverter } from "../viewer/js/utilities";
import { useTranslation } from "react-i18next";
import FolderIcon from "@material-ui/icons/Folder";
import {AttachFile, Delete, Save} from "@material-ui/icons";
import { MenuDivider } from "./menu-items";
import isHtml from 'is-html';

const converter = new UnitConverter();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    flexibleHeight: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "& span": {
        flex: 1
      }
    },
    filePath: {
      wordBreak: "break-word"
    },
    fileButtonDiv: {
      paddingTop: theme.spacing(1),
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    dropdownWithLabel: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      "& span": {
        flex: 1
      }
    },
    paddingBottom: {
      paddingBottom: `${theme.spacing(1)}px !important`,
    },
    inputParent: {
      width: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      minHeight: theme.spacing(3),
      paddingBottom: theme.spacing(0.5)
    },
    inputLabel: {
      height: "fit-content"
    },
    inputSwitch: {
      marginTop: "-0.25rem"
    },
    inputFolder: {
      padding: theme.spacing(0.5),
      marginBottom: theme.spacing(-0.5)
    },
    error: {
      color: theme.palette.error.main
    },
    warning: {
      color: theme.palette.warning.main
    },
  })
);

// TODO: add props for NumberField / NumberFieldWithLabel

export const NumberField = (props) => {
  const {units=false, ...other} = props;

  return units
    ? <NumberFieldWithUnits {...other}/>
    : <NumberFieldNoUnits {...other}/>;
};

export const NumberFieldWithLabel = (props) => {
  const {title, label, ...other} = props;

  const classes = useStyles();

  return (
    <div className={classes.flexibleHeight}>
      <div className={classes.inputParent}>
        <InputLabel>{title}</InputLabel>
      </div>

      <Typography variant="caption">
        {label}
      </Typography>

      <NumberField {...other}/>
    </div>
  );
};

const NumberFieldNoUnits = (props) => {
  const {data, setData, min, max, adornment, step=1.0, index=0, ...other} = props;

  const {t} = useTranslation();

  // NOTE: Space character is required for even positioning in UI
  const helperText = data.error
    ? t('dialog.accepted-range-is-min-max', {min, max})
    : undefined;

  const endAdornment = adornment
    ? <InputAdornment position="end">{adornment}</InputAdornment>
    : null;

  return (
    <TextField
      type="number"
      error={data.error}
      helperText={helperText}
      {...other}
      fullWidth
      InputProps={{
        inputProps: {max, min, step},
        endAdornment
      }}
      value={data.value}
      onChange={(event) => {
        let hasError = false;

        let floatValue = parseFloat(event.target.value);
        if (isNaN(floatValue)) {
          hasError = true;
        }

        if (floatValue > max) {
          hasError = true;
        } else if (floatValue < min) {
          hasError = true;
        }

        setData({
          value: event.target.value,
          error: hasError,
          index: index
        });
      }}
    />
  );
};

const NumberFieldWithUnits = (props) => {
  const {data, min, max, setData, step=1.0, ...other} = props;
  const units = LocalScene.viewProjectionUnits;

  const [convertedMin, setConvertedMin] = useState(0);
  const [convertedMax, setConvertedMax] = useState(0);
  const [convertedData, setConvertedData] = useState({
    value: 0,
    error: false
  });

  const convertNearestStep = (value, roundFunc) => {
    if (units === "m") {
      return value;
    };

    const converted = converter.convert(value, "m", units, 2);
    let numSteps = roundFunc(converted / step);
    return parseFloat((numSteps * step).toFixed(2));
  };

  useEffect(() => {
    // Initial conversion from meters to new units
    setConvertedMin(convertNearestStep(min, Math.floor));
    setConvertedMax(convertNearestStep(max, Math.ceil));
    setConvertedData({
      value: convertNearestStep(data.value, Math.floor),
      error: data.error
    });
  }, []);

  useEffect(() => {
    // Update the original state
    setData({...convertedData});
  }, [convertedData]);

  return (
    <NumberFieldNoUnits
      {...other}
      data={convertedData}
      setData={setConvertedData}
      min={convertedMin}
      max={convertedMax}
      adornment={units}
    />
  );
};

interface DropdownWithLabelProps {
  title: string;
  label?: string;
  value: string | number;
  onChange: Function;
  disabled?: boolean;
  seperator?: number[]
  options: [value: any, text: string][];
}

export const DropdownWithLabel = (props: DropdownWithLabelProps) => {
  const {title, label, value, onChange, disabled=false,
    seperator, options} = props;

  const classes = useStyles();

  let menuOptions = [];

  options.forEach((data, index) => {
    const [value, text] = data;

    menuOptions.push((
      <MenuItem key={index} value={value}>
        {text}
      </MenuItem>
    ));

    const hasSeperator = seperator && seperator.includes(index);
    const isLastItem = index === options.length - 1;

    if (hasSeperator && !isLastItem) {
      menuOptions.push((
        <MenuDivider key={`divider-${index}`}/>
      ));
    }
  });

  return (
    <div className={clsx(classes.dropdownWithLabel, classes.paddingBottom, classes.flexibleHeight)}>
      <div className={classes.inputParent}>
        <InputLabel>
          {title}
        </InputLabel>
      </div>

      {label && <Typography variant="caption">
        {label}
      </Typography>}

      <Select
        value={value}
        disabled={disabled}
        onChange={event => {
          onChange(event.target.value);
        }}
      >
        {menuOptions}
      </Select>
    </div>
  );
};

interface FolderWithLabelProps {
  title: string;
  label?: string;
  value: string | number;
  onChange: Function;
}

export const FolderWithLabel = (props: FolderWithLabelProps) => {
  const {title, label, value, onChange} = props;
  const classes = useStyles();

  const handleFolderChange = async () => {
    const result = await dialog.showOpenDialog({
      properties: ['openDirectory']
    });

    if (result.canceled) return;
    let folderPath = slash(result.filePaths[0]);
    onChange(folderPath);
  };

  return (
    <div className={classes.paddingBottom}>
      <div className={classes.inputParent}>
        <InputLabel className={classes.inputLabel}>
          {title}
        </InputLabel>

        <IconButton
          className={classes.inputFolder}
          onClick={handleFolderChange}
        >
          <FolderIcon fontSize="small"/>
        </IconButton>
      </div>

      <Typography variant="caption" className={classes.filePath}>
        {value ? value : label}
      </Typography>
    </div>
  );
};

interface FileWithLabelProps {
  title: string;
  label?: string;
  filter: {name: string; extensions: string[]}[]
  value: string | number;
  onChange: Function;
  optional?: boolean;
  saveFile?: boolean;
}

export const FileWithLabel = (props: FileWithLabelProps) => {
  const {title, label, filter, value, onChange,
    optional=false, saveFile=false} = props;

  const classes = useStyles();

  const handleFileChange = () => {
    dialog.showOpenDialog({
      title: title,
      properties: ['openFile'],
      filters: filter
    }).then(result => {
      if (result.canceled) {
        return;
      }

      let folderPath = slash(result.filePaths[0]);
      onChange(folderPath);
    });
  };

  const handleSaveFileChange = () => {
    dialog.showSaveDialog({
      title: title,
      filters: filter
    }).then(result => {
      if (result.canceled) {
        return;
      }
      onChange(result.filePath);
    });
  };

  const handleChange = saveFile
    ? handleSaveFileChange
    : handleFileChange;

  return (
    <div className={clsx(classes.paddingBottom, classes.flexibleHeight)}>
      <div className={classes.inputParent}>
        <InputLabel className={classes.inputLabel}>
          {title}
        </InputLabel>

        <div className={classes.inputFolder}>
          {value && optional && (<IconButton
            onClick={() => {
              onChange(null);
            }}
          >
            <Delete fontSize="small"/>
          </IconButton>)}

          <IconButton
            onClick={handleChange}
          >
            {saveFile ? <Save fontSize="small"/>
              : <AttachFile fontSize="small"/>}
          </IconButton>
        </div>

      </div>

      <Typography
        variant="caption"
        className={clsx(classes.filePath, {
          [classes.warning]: !value && optional
        })}
      >
        {value ? value : label}
      </Typography>
    </div>
  );
};

interface InputWithLabelProps {
  title: string;
  label?: string | JSX.Element;
  value: string | number;
  onChange: Function;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  multiline?: boolean;
  placeholder?: string;
}

export const InputWithLabel = (props: InputWithLabelProps) => {
  const classes = useStyles();
  const {value, title, onChange, label, required=true,
    disabled=false, error=false, ...other} = props;

  const [touched, setTouched] = useState(false);

  const onInputChange = useCallback((event) => {
    onChange(event.target.value);
  }, [onChange]);

  const onBlur = useCallback(() => {
    setTouched(true);
  }, []);

  return (
    <div className={classes.paddingBottom}>
      <div className={classes.inputParent}>
        <InputLabel>{title}</InputLabel>
      </div>

      <Input
        fullWidth
        error={(touched && error) || (required && touched && (value === ""))}
        value={value}
        disabled={disabled}
        onChange={onInputChange}
        onBlur={onBlur}
        {...other}
      />

      <Typography variant="caption">
        {label}
      </Typography>
    </div>
  );
};

interface TextWithLabelProps {
  title: string
  value?: string
  label?: string | JSX.Element
}

export const TextWithLabel = (props: TextWithLabelProps) => {
  const {title, label, value} = props;

  const classes = useStyles();
  const theme = useTheme();
  const renderAsHTML = isHtml(value);

  return (
    <div className={classes.flexibleHeight}>
      <div className={classes.inputParent}>
        <InputLabel>{title}</InputLabel>
      </div>

      {value && <React.Fragment>
        {renderAsHTML && <div
          style={theme.typography.body1}
          dangerouslySetInnerHTML={{
            __html: value
          }}
        />}

        {!renderAsHTML && <Typography>
          {value}
        </Typography>}
      </React.Fragment>}

      {label && <Typography variant="caption">
        {label}
      </Typography>}
    </div>
  );
};

interface CheckboxWithLabelProps {
  title: string;
  label?: string;
  value: boolean;
  onChange: Function;
}

export const CheckboxWithLabel = (props: CheckboxWithLabelProps) => {
  const {title, label, value, onChange} = props;

  const classes = useStyles();

  const onCheckboxChange = useCallback((_, checked) => {
    onChange(checked);
  }, [onChange]);

  return (
    <div className={classes.paddingBottom}>
      <div className={classes.inputParent}>
        <InputLabel className={classes.inputLabel}>
          {title}
        </InputLabel>

        <Switch
          checked={value}
          size="small"
          color="secondary"
          className={classes.inputSwitch}
          onChange={onCheckboxChange}
        />
      </div>

      <Typography variant="caption">
        {label}
      </Typography>
    </div>
  );
};